import typeCheckUtils from '../../utils/typeCheckUtils';

import AirConditioningTypes from './AirConditioningTypes';
import ApplianceTypes from './ApplianceTypes';
import CommunicationTypes from './CommunicationTypes';
import CommunityRoomTypes from './CommunityRoomTypes';
import FloorCoveringTypes from './FloorCoveringTypes';
import HeatingSourceTypes from './HeatingSourceTypes';
import LeaseLengths from './LeaseLengths';
import OutdoorCommonAreaTypes from './OutdoorCommonAreaTypes';
import ParkingTypes from './ParkingTypes';
import SecurityTypes from './SecurityTypes';
import SportCourtTypes from './SportCourtTypes';
import UtilitiesIncluded from './UtilitiesIncluded';
import ViewTypes from './ViewTypes';

import type { AirConditioningTypesProps } from './AirConditioningTypes';
import type { ApplianceTypesProps } from './ApplianceTypes';
import type { CommunicationTypesProps } from './CommunicationTypes';
import type { CommunityRoomTypesProps } from './CommunityRoomTypes';
import type { FloorCoveringTypesProps } from './FloorCoveringTypes';
import type { HeatingSourceTypesProps } from './HeatingSourceTypes';
import type { LeaseLengthsProps } from './LeaseLengths';
import type { OutdoorCommonAreaTypesProps } from './OutdoorCommonAreaTypes';
import type { ParkingTypesProps } from './ParkingTypes';
import type { SecurityTypesProps } from './SecurityTypes';
import type { SportCourtTypesProps } from './SportCourtTypes';
import type { UtilitiesIncludedProps } from './UtilitiesIncluded';
import type { ViewTypesProps } from './ViewTypes';

export type BuildingAttributesProps = {
  administrativeFee: number | null;
  airConditioningTypes: AirConditioningTypesProps;
  applianceTypes: ApplianceTypesProps;
  applicationFee: number | null;
  buildingName: string;
  busesNearby: string;
  communicationTypes: CommunicationTypesProps;
  communityRoomTypes: CommunityRoomTypesProps;
  depositFeeMax: number | null;
  depositFeeMin: number | null;
  floorCoveringTypes: FloorCoveringTypesProps;
  hasAssistedLiving: boolean;
  hasBarbecue: boolean;
  hasBicycleStorage: boolean;
  hasCeilingFan: boolean;
  hasDisabledAccess: boolean;
  hasDryCleaningDropOff: boolean;
  hasElevator: boolean;
  hasFireplace: boolean;
  hasGuestSuite: boolean;
  hasHotTub: boolean;
  hasOnlineMaintenancePortal: boolean;
  hasOnlineRentPayment: boolean;
  hasOnsiteManagement: boolean;
  hasPackageService: boolean;
  hasPatioBalcony: boolean;
  hasPetPark: boolean;
  hasSauna: boolean;
  hasSharedLaundry: boolean;
  hasSpanishSpeakingStaff: boolean;
  hasStorageSpace: boolean;
  hasSwimmingPool: boolean;
  hasTwentyFourHourMaintenance: boolean;
  hasValetTrash: boolean;
  heatingSourceTypes: HeatingSourceTypesProps;
  isFurnished: boolean;
  isIncomeRestricted: boolean;
  isOver55: boolean;
  leaseLengths: LeaseLengthsProps;
  nearbyAmenity1: string;
  nearbyAmenity2: string;
  nearbyAmenity3: string;
  nearbyAmenity4: string;
  numUnits?: number;
  outdoorCommonAreaTypes: OutdoorCommonAreaTypesProps;
  parkingDescription: string;
  parkingRentDescription: string;
  parkingTypes: ParkingTypesProps;
  petPolicyDescription: string;
  railNearby: string;
  securityTypes: SecurityTypesProps;
  smokingAllowed: boolean;
  sportCourtTypes: SportCourtTypesProps;
  utilitiesIncluded: UtilitiesIncludedProps;
  viewTypes: ViewTypesProps;
  zillowDescription: string;
};

const BuildingAttributes = ({
  administrativeFee = null,
  airConditioningTypes,
  applianceTypes,
  applicationFee = null,
  buildingName = '',
  busesNearby = '',
  communicationTypes,
  communityRoomTypes,
  depositFeeMax = null,
  depositFeeMin = null,
  floorCoveringTypes,
  hasAssistedLiving = false,
  hasBarbecue = false,
  hasBicycleStorage = false,
  hasCeilingFan = false,
  hasDisabledAccess = false,
  hasDryCleaningDropOff = false,
  hasElevator = false,
  hasFireplace = false,
  hasGuestSuite = false,
  hasHotTub = false,
  hasOnlineMaintenancePortal = false,
  hasOnlineRentPayment = false,
  hasOnsiteManagement = false,
  hasPackageService = false,
  hasPatioBalcony = false,
  hasPetPark = false,
  hasSauna = false,
  hasSharedLaundry = false,
  hasSpanishSpeakingStaff = false,
  hasStorageSpace = false,
  hasSwimmingPool = false,
  hasTwentyFourHourMaintenance = false,
  hasValetTrash = false,
  heatingSourceTypes,
  isFurnished = false,
  isIncomeRestricted = false,
  isOver55 = false,
  leaseLengths,
  nearbyAmenity1 = '',
  nearbyAmenity2 = '',
  nearbyAmenity3 = '',
  nearbyAmenity4 = '',
  numUnits,
  outdoorCommonAreaTypes,
  parkingDescription = '',
  parkingRentDescription = '',
  parkingTypes,
  petPolicyDescription = '',
  railNearby = '',
  securityTypes,
  smokingAllowed = false,
  sportCourtTypes,
  utilitiesIncluded,
  viewTypes,
  zillowDescription = '',
}: Partial<BuildingAttributesProps> = {}): BuildingAttributesProps => ({
  administrativeFee,
  airConditioningTypes: AirConditioningTypes(airConditioningTypes),
  applianceTypes: ApplianceTypes(applianceTypes),
  applicationFee,
  buildingName: String(buildingName),
  busesNearby: String(busesNearby),
  communicationTypes: CommunicationTypes(communicationTypes),
  communityRoomTypes: CommunityRoomTypes(communityRoomTypes),
  depositFeeMax,
  depositFeeMin,
  floorCoveringTypes: FloorCoveringTypes(floorCoveringTypes),
  hasAssistedLiving: Boolean(hasAssistedLiving),
  hasBarbecue: Boolean(hasBarbecue),
  hasBicycleStorage: Boolean(hasBicycleStorage),
  hasCeilingFan: Boolean(hasCeilingFan),
  hasDisabledAccess: Boolean(hasDisabledAccess),
  hasDryCleaningDropOff: Boolean(hasDryCleaningDropOff),
  hasElevator: Boolean(hasElevator),
  hasFireplace: Boolean(hasFireplace),
  hasGuestSuite: Boolean(hasGuestSuite),
  hasHotTub: Boolean(hasHotTub),
  hasOnlineMaintenancePortal: Boolean(hasOnlineMaintenancePortal),
  hasOnlineRentPayment: Boolean(hasOnlineRentPayment),
  hasOnsiteManagement: Boolean(hasOnsiteManagement),
  hasPackageService: Boolean(hasPackageService),
  hasPatioBalcony: Boolean(hasPatioBalcony),
  hasPetPark: Boolean(hasPetPark),
  hasSauna: Boolean(hasSauna),
  hasSharedLaundry: Boolean(hasSharedLaundry),
  hasSpanishSpeakingStaff: Boolean(hasSpanishSpeakingStaff),
  hasStorageSpace: Boolean(hasStorageSpace),
  hasSwimmingPool: Boolean(hasSwimmingPool),
  hasTwentyFourHourMaintenance: Boolean(hasTwentyFourHourMaintenance),
  hasValetTrash: Boolean(hasValetTrash),
  heatingSourceTypes: HeatingSourceTypes(heatingSourceTypes),
  isFurnished: Boolean(isFurnished),
  isIncomeRestricted: Boolean(isIncomeRestricted),
  isOver55: Boolean(isOver55),
  leaseLengths: LeaseLengths(leaseLengths),
  nearbyAmenity1: String(nearbyAmenity1),
  nearbyAmenity2: String(nearbyAmenity2),
  nearbyAmenity3: String(nearbyAmenity3),
  nearbyAmenity4: String(nearbyAmenity4),
  numUnits: typeCheckUtils.Number(numUnits) ? Number(numUnits) : undefined,
  outdoorCommonAreaTypes: OutdoorCommonAreaTypes(outdoorCommonAreaTypes),
  parkingDescription: String(parkingDescription),
  parkingRentDescription: String(parkingRentDescription),
  parkingTypes: ParkingTypes(parkingTypes),
  petPolicyDescription: String(petPolicyDescription),
  railNearby: String(railNearby),
  securityTypes: SecurityTypes(securityTypes),
  smokingAllowed: Boolean(smokingAllowed),
  sportCourtTypes: SportCourtTypes(sportCourtTypes),
  utilitiesIncluded: UtilitiesIncluded(utilitiesIncluded),
  viewTypes: ViewTypes(viewTypes),
  zillowDescription: String(zillowDescription),
});

export default BuildingAttributes;
